import { EmptyStateNoResults, Pagination, SearchInput, ListItemLink, List, ListItemContent, InlineEmptyStateOnboarding } from "@octopusdeploy/design-system-components";
import type { PrimaryPageAction, PageAction } from "@octopusdeploy/design-system-components";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ResourceCollection, ProxyResource, Repository } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useCallback } from "react";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { formatUrl } from "~/components/Navigation/ExternalLink/ExternalLink";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import ListTitle from "~/primitiveComponents/dataDisplay/ListTitle/ListTitle";
export interface MachineProxiesPageProps {
    spaceId: string;
    queryParams: MachineProxiesQueryParams;
    setQueryParams: QueryParamValuesSetter<MachineProxiesQueryParams>;
    loaderData: MachineProxiesPageLoaderData;
}
export interface MachineProxiesPageLoaderData {
    machineProxies: ResourceCollection<ProxyResource>;
    hasMachineProxies: boolean;
}
export interface MachineProxiesQueryParams {
    machineProxyName: string;
    page: number | undefined;
    pageSize: number | undefined;
}
export async function machineProxiesPageLoader(repository: Repository, queryParams: MachineProxiesQueryParams): Promise<MachineProxiesPageLoaderData> {
    return getPaginatedMachineProxies(repository, queryParams);
}
const PageTitle = "Machine Proxies";
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
export function ProxiesPage({ spaceId, loaderData, queryParams, setQueryParams }: MachineProxiesPageProps) {
    const { result: machineProxiesResult } = usePaginatedMachineProxiesQuery(loaderData, queryParams);
    const [debouncedMachineProxyName, setDebouncedMachineProxyName] = useDebouncedExternalState(queryParams.machineProxyName, (value) => setQueryParams({ machineProxyName: value, page: 1 }), 200);
    const { hasMachineProxies, machineProxies } = machineProxiesResult;
    const addProxyPageAction: PrimaryPageAction = {
        type: "navigate",
        label: "Add Proxy",
        path: links.createProxyPage.generateUrl({ spaceId: spaceId }),
        hasPermissions: isAllowed({
            permission: Permission.ProxyCreate,
        }),
    };
    const modifyWebProxyPageAction: PageAction = {
        type: "navigate",
        label: "Modify Web Request Proxy",
        path: links.webRequestProxySettingsPage.generateUrl(),
        buttonType: "secondary",
        hasPermissions: isAllowed({
            permission: Permission.ProxyCreate,
        }),
        extraContext: "Modify the proxy used by the Octopus Server for web requests",
    };
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    if (!hasMachineProxies) {
        return (<PageContent header={{ title: PageTitle, primaryAction: addProxyPageAction }}>
                <BorderedContent>
                    <InlineEmptyStateOnboarding entityName={"Machine Proxies"} primaryAction={addProxyPageAction} learnMore={{ path: formatUrl("ProxySupport"), label: "Docs: Machine Proxies" }}/>
                </BorderedContent>
            </PageContent>);
    }
    const hideBottomPagination = machineProxies.Items.length < 20;
    return (<PageContent header={{ title: PageTitle, primaryAction: addProxyPageAction, pageActions: [modifyWebProxyPageAction] }} pagination={{
            ui: (<Pagination label={"Proxies"} totalResults={machineProxies.TotalResults} itemsPerPageOptions={pageSizeOptions} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} selectedItemsPerPage={machineProxies.ItemsPerPage}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }} filters={{ inputs: [<SearchInput autoFocus={true} value={debouncedMachineProxyName} onChange={setDebouncedMachineProxyName} accessibleName={"Search machine proxy by name"} placeholder={"Search by name"}/>] }}>
            <BorderedContent>
                <List<ProxyResource> items={machineProxies.Items} renderRow={({ item: machineProxy }) => <MachineProxyListItem spaceId={spaceId} machineProxy={machineProxy}/>} empty={<EmptyStateNoResults />}/>
            </BorderedContent>
        </PageContent>);
}
function MachineProxyListItem({ spaceId, machineProxy }: {
    spaceId: string;
    machineProxy: ProxyResource;
}) {
    return (<ListItemLink href={links.editProxyPage.generateUrl({ spaceId: spaceId, proxyId: machineProxy.Id })}>
            <ListItemContent>
                <ListTitle>{machineProxy.Name}</ListTitle>
            </ListItemContent>
        </ListItemLink>);
}
interface GetPaginatedMachineProxiesOptions {
    page: number | undefined;
    pageSize: number | undefined;
    machineProxyName: string | undefined;
}
async function getPaginatedMachineProxies(repository: Repository, { page = 1, pageSize = defaultPageSize, machineProxyName = "" }: GetPaginatedMachineProxiesOptions) {
    const proxies = repository.Proxies.list({
        skip: (page - 1) * pageSize,
        take: pageSize,
        partialName: machineProxyName,
    });
    const proxyStats = repository.Proxies.list({
        skip: 0,
        take: 0,
    });
    return {
        machineProxies: await proxies,
        hasMachineProxies: (await proxyStats).TotalResults > 0,
    };
}
function usePaginatedMachineProxiesQuery(initialResult: MachineProxiesPageLoaderData, queryParams: MachineProxiesQueryParams) {
    const { page, pageSize, machineProxyName } = queryParams;
    return useQuery((repository) => getPaginatedMachineProxies(repository, { page, pageSize, machineProxyName }), [page, pageSize, machineProxyName], "Get paginated machine proxies", { initialResult });
}
